import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Suspense, type SyntheticEvent, lazy, useEffect, useMemo } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { type NavigateFunction } from 'react-router';
import { type Car, type Filter } from '../../_types/Car';
import { useRootContainerContext } from '../../context/root-container';
import { useSettingsContext } from '../../context/settings';
import { useSessionStorage } from '../../hooks/use-storage';
import useLocales from '../../hooks/useLocales';
import { Modules } from '../../modules/_definition/types';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import getStyleQuery from '../../utils/tools/getStyleQuery';
import { CalcFdType } from '../calculator/types';
import { getLowestRate } from '../car-price-grid/helpers';
import type { LowestRate } from '../car-price-grid/types';
import CarTileGridSkeleton from '../car-tile/car-tile-grid-skeleton';
import CarTileListMiniSkeleton from '../car-tile/car-tile-list-mini-skeleton';
import CarTileListSkeleton from '../car-tile/car-tile-list-skeleton';

const CarTileGrid = lazy(() => import('../car-tile/car-tile-grid' /* webpackChunkName: "CarTileGrid" */));
const CarTileList = lazy(() => import('../car-tile/car-tile-list' /* webpackChunkName: "CarTileList" */));
const CarTileListMini = lazy(() => import('../car-tile/car-tile-list-mini' /* webpackChunkName: "CarTileListMini" */));

type CarListViewEndlessProps = {
    cars: Record<string, Car<false>>;
    filter: Filter;
    list?: boolean;
    loading: boolean;
    mini?: boolean;
    module: Modules;
    numFound: number;
    onFilters: UseFormSetValue<Filter>;
    onMiniSelectCar?: ((e: SyntheticEvent<any>, car: Car<false>) => void) | undefined;
} & (
        | {
            mini?: true;
            onMiniSelectCar?: (e: SyntheticEvent<any>, car: Car<false>) => void;
        }
        | {
            mini?: false;
            navigate: NavigateFunction;
            onMiniSelectCar?: undefined;
        }
    );

export default function CarListViewEndless(props: CarListViewEndlessProps) {
    const { cars, filter, list, loading, mini, module, numFound, onFilters, onMiniSelectCar } = props;
    const rootContainer = useRootContainerContext();
    const { state: sessionSearch } = useSessionStorage('search');
    const settings = useSettingsContext();
    const { translate } = useLocales();
    const CarTileElement = useMemo(() => (mini ? CarTileListMini : list ? CarTileList : CarTileGrid), [list, mini]);
    const CarTileSkeletonElement = useMemo(() => (mini ? CarTileListMiniSkeleton : list ? CarTileListSkeleton : CarTileGridSkeleton), [list, mini]);

    const hasMore = useMemo(() => numFound > (filter?.moreCars * filter?.pageSize), [filter?.moreCars, filter?.pageSize, numFound]);

    // Needed when going back from car detail page to list
    useEffect(() => {
        const searchParams = new URLSearchParams(location?.search);
        const pageNrFromUrl = searchParams.get('moreCars');

        if (!checkUndefinedNullOrEmpty(pageNrFromUrl) && checkUndefinedNullOrEmpty(sessionSearch?.moreCars)) {
            onFilters('moreCars', parseInt(pageNrFromUrl));
        }
    }, [onFilters, sessionSearch?.moreCars]);

    return (<>
        <Box
            display="grid"
            gap={2}
            gridTemplateColumns={
                list || mini
                    ? 'repeat(1, 1fr)'
                    : {
                        xs: 'repeat(1, 1fr)',
                        sm: settings?.[module]?.limitCols > 1 ? 'repeat(2, 1fr)' : undefined,
                        md: settings?.[module]?.limitCols > 2 ? 'repeat(3, 1fr)' : undefined,
                        xl: settings?.[module]?.limitCols > 3 ? 'repeat(4, 1fr)' : undefined,
                    }
            }
            gridTemplateRows="repeat(1, 1fr)"
            sx={{
                maxHeight: '100%',
                overflow: 'visible',
            }}
        >
            {Object?.keys(cars || {})?.map((idCar, index) => {
                const car = cars[idCar];
                return (
                    <Suspense key={car.id} fallback={<CarTileSkeletonElement rootContainer={rootContainer} settings={settings} />}>
                        <CarTileElement
                            car={car}
                            loading={loading}
                            module={module}
                            navigate={mini === false ? props?.navigate : undefined}
                            onMiniClick={onMiniSelectCar}
                            showMonthlyRate={
                                module === Modules?.Finanzierungsansicht
                                    ? !checkUndefinedNullOrEmpty(car?.finanzierung)
                                        ? { type: CalcFdType.Financing, rate: car?.finanzierung?.monatliche_rate }
                                        : undefined
                                    : !checkUndefinedNullOrEmpty(filter?.fd) || !checkUndefinedNullOrEmpty(filter?.Rate)
                                        ? getLowestRate(
                                            car,
                                            !checkUndefinedNullOrEmpty(filter?.fd)
                                                ? [
                                                    !filter?.fd?.includes(1) ? CalcFdType.Financing : undefined,
                                                    ...(!filter?.fd?.includes(2) ? [CalcFdType.Leasing, 'leasingComm' as LowestRate['type']] : []),
                                                ]?.filter((e) => !checkUndefinedNullOrEmpty(e))
                                                : undefined
                                        )
                                        : undefined
                            }
                            sourceList={0}
                            sourceIndex={index}
                        />
                    </Suspense>
                );
            })}
        </Box>

        {/* Load more cars Button */}
        {hasMore
            && <Box display="flex" width="100%" alignItems="center" justifyContent="center" my={3}>
                <Button
                    data-testid="CarListViewEndless_LoadMoreBtn"
                    key="CarListViewEndless_LoadMoreBtn"
                    color="primary"
                    size="large"
                    disabled={!hasMore}
                    variant="contained"
                    onClick={function (e) {
                        e?.preventDefault();
                        onFilters('moreCars', filter?.moreCars + 1);
                    }}
                    sx={{
                        [getStyleQuery()]: {
                            'outline': 'none',
                            'transitionDuration': '250ms, 0ms, 250ms, 250ms', // transition-duration 0ms for box-shadow problem Safari
                            '&:hover': { outline: 'none !important', boxShadow: 'none !important' },
                        },
                    }}
                >
                    {translate('common:buttons.loadMoreVehiclesVariant')}
                </Button>
            </Box>
        }
    </>);
}
